import React, { useEffect, useRef } from "react";
import { css } from "styled-components";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import GridContainer, { GridContainerProps, GridOffset, GridWrapper } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import ADIColors from "@NA/Utils/ADIColors";
import translate from "@insite/client-framework/Translate";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import { StyledButton } from "@insite/mobius/Clickable";
import { GetWishListsApiParameter } from "@insite/client-framework/Services/WishListService";
import { getWishListsDataView } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadWishLists from "@insite/client-framework/Store/Data/WishLists/Handlers/LoadWishLists";
import { connect, ResolveThunks, useSelector } from "react-redux";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";
import RecentWishListsIcon from "@NA/Icons/RecentWishListsIcon";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { checkVaildUserForHmiRewards } from "@NA/Utils/Utility";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";

const enum fields {
    numberOfRecords = "numberOfRecords",
    pageType = "pageType",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.pageType]: string;
    };
}

const mapStateToProps = (state: CustomApplicationState) => {
    const session = getSession(state);
    const adiUserRoles = state.ADIGlobalData.ADIUserRoleStore;
    return {
        recentWishListsDataView: getWishListsDataView(state, recentWishListsParameter),
        myListsPageNavLink: getPageLinkByPageType(state, "MyListsPage"),
        isAuthenticated: state.context.session.isAuthenticated,
        isMylistAccess: (adiUserRoles.fullAccess && adiUserRoles.accounting) || adiUserRoles.fullAccess || adiUserRoles.administrator,
    };
};

const setData = (wishList: WishListModel) => ({
    type: "Custom/CompleteLoadWishListData",
    isHomePage: true,
    result: wishList,
});

let recentWishListsParameter: GetWishListsApiParameter = {
    wishListLinesSort: "mostRecent",
};

const mapDispatchToProps = {
    loadWishLists,
    setData,
};

interface StateProps {
    recentWishListsDataView: ReturnType<typeof getWishListsDataView>;
    myListsPageNavLink: ReturnType<typeof getPageLinkByPageType>;
    isAuthenticated: boolean;
    isMylistAccess: boolean;
}

export interface ADImyListStyles {
    myListGridContainer?: InjectableCss;
    myListGridCard?: InjectableCss;
    myListTitleContainer?: InjectableCss;   
    myListTitle?: InjectableCss;
    myListCardContainer?: InjectableCss;
    myListContentTitle?: InjectableCss;
    myListContentDescription?: InjectableCss;
    myListBtnStyle?: InjectableCss;
    myListBtnGriditem?: InjectableCss;
    myListContentGridItem?: InjectableCss;
    myListContentTittle?: InjectableCss;
    addToListName?: InjectableCss;
    myListContentDivider?: InjectableCss;
    myListCardGridContainer?: InjectableCss;
    myListContenDetails?: InjectableCss;
}

const ADImyListStyles: ADImyListStyles = {
    myListGridContainer: {
        css: css`
            margin: 0 auto;
            padding: 15px 0;
            gap: 20px;
            display: flex;
            flex-direction: column;
            flex: 1;
            height: 100%;
            position: relative;

            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`flex-direction: column;`,
                    css`flex-direction: column;`,
                    null,
                    null,
                    null,
                ])}
                ${GridWrapper} {
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            null,
                            null,
                            css`
                                max-width:100%;
                            `,
                            null,
                            null,
                        ])}
                }
        `,
    },
    myListGridCard: {
        css: css`
        flex:1;
        min-height:388px;
        border-radius: 6px;
        background-color: #fff;
        border-radius: 6px;
        border: solid 1px #bbb;
        ${({ theme }) =>
        breakpointMediaQueries(theme, [
            css`min-height:auto;`,
            css`min-height:auto;`,
            css`min-height:auto;`,
            null,
            null,
        ])}                
        > ${GridOffset} {
         width:100%;
         margin:0px;
         height: 100%;
        }
    `,
    },
    myListTitleContainer: {
        css: css`
        border-radius: 6px;
        border-bottom-left-radius:0px;
        border-bottom-right-radius:0px;
        padding: 15px 20px !important;
        background-color: #006892;
        width:100%;
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        > svg {
            ${({ theme }) =>
            breakpointMediaQueries(theme, [
                css`width:28px;height:28px;`,
                css`width:28px;height:28px;`,
                null,
                null,
                null,
            ])} 
        }
    `,
    },
    myListTitle: {
        css: css`
        font-size: 20px;
        font-weight: bold;
        line-height: normal;
        color: #fff;
        margin-left:10px;
        ${({ theme }) =>
        breakpointMediaQueries(theme, [
            css`font-size: 16px;`,
            css`font-size: 16px;`,
            css`font-size: 18px;`,
            null,
            null,
        ])} 
    `,
    },
    myListCardContainer: {
        css: css`
        padding:15px 20px 20px;
        height:calc(100% - 65px);
        border-top-left-radius:0px;
        border-top-right-radius:0px;
        border-top:none;
        display: flex;
        flex-direction: column;
        justify-content:flex-start;
        align-items: flex-start;
        width: 100%;
        margin: 0 auto;
    `,
    },
    myListCardGridContainer: {
        css: css`
        > ${GridOffset} {
         width:100%;
         margin:0px;
         height: 100%;
         display: flex;
         flex-direction: column;
            ${GridItemStyle}{
                max-width: 100%;
                width:100%;
                padding: 0px;
                flex-basis:initial;
                flex-direction: column;
            }
        }   
    `,
    },
    myListContentTittle: {
        css: css`
            font-size: 14px;
            font-weight: 600;
            line-height: 1.2;
            color: ${ADIColors.darkBlack3};    
    `,
    },
    addToListName: {
        css: css`
            font-size: 14px;
            font-weight: 600;
            line-height: 1.2;
            color: ${ADIColors.darkBlack3}; 
            pointer-events: none;
            text-decoration:none;
            &:hover{
                text-decoration:none;
                pointer-events: none;
            }
            &:active{
                text-decoration:none;
                pointer-events: none;
            } 
    `,
    },
    myListContentDivider: {
        css: css`
            font-size: 14px;
            font-weight: 600;
            line-height: 1.2;
            color: ${ADIColors.darkBlack3}; 
            padding: 0 5px 0 7px;
            position: relative;
            bottom: 1px;  
    `,
    },
    myListContenDetails: {
        css: css`
        font-size: 14px;
        font-weight: 600;
        line-height: normal;
        ${StyledButton} {
            ${TypographyStyle} {
                font-size: 14px;
                color: ${ADIColors.darkBlack3};
                text-decoration: underline; 
            } 
            svg {
                height: 16px;
                width: 16px;
                position: relative;
                stroke-width: 3;
                top: 3px;
                right: 0px;
                color: ${ADIColors.darkBlack3};
            }
            &:hover{
                ${TypographyStyle} {
                    color: ${ADIColors.darkBlack3};
                } 
            }
        }
    `,
    },
    myListContentTitle: {
        css: css`
        font-size: 20px;
        font-weight: bold;
        line-height: normal;
         color: ${ADIColors.darkBlack3};
        margin-bottom:10px;
        display: block;
        ${({ theme }) =>
        breakpointMediaQueries(theme, [
            css`font-size: 16px;`,
            css`font-size: 16px;`,
            css`font-size: 18px;`,
            null,
            null,
        ])} 
    `,
    },
    myListContentDescription: {
        css: css`
        font-size: 14px;
        font-weight: normal;
        line-height: normal;
        color: ${ADIColors.darkBlack3};
        padding-bottom:24px;
        display: block;
    `,
    },
    myListContentGridItem: {
        css: css`           
        flex-direction: row;
        justify-content: space-between;
        border-top: 1px solid #ddd;
        border-bottom: 1px solid #ddd;
        padding: 9px 0 9px!important;
        width: 100%;
        display: flex;
        align-items: baseline;

        &:last-child {
            border-top: 0;
        }
        &:first-child:last-child {
            border-top: 1px solid #ddd;
        }
    `,
    },
    myListBtnGriditem: {
        css: css`    
        display: flex;
        width:100%;
    `,
    },
    myListBtnStyle: {
        css: css`
        padding: 10px;
        border-radius: 4px;
        border: solid 1px #004da6;
        background-color: #fff;
        min-height: 40px;
        line-height: 1;
        width: calc(100% - 40px);
        margin: 0 20px 20px;
        position: absolute;
        left: 0;
        bottom: 16px;

        ${({ theme }) => { return breakpointMediaQueries(
            theme, [null, null, 
            css` width: 100%; 
                 margin: 0;
                 bottom: 0;
                 position: relative;
                 margin-top: 20px;`
            ,],"max",); 
        }}

        &:hover {
                background-color: #c8ddf6;
        }
        ${TypographyStyle}  {
            color: ${ADIColors.blue1};
            font-size: 16px;
            font-weight: bold;
            margin: auto;
            ${({ theme }) =>
            breakpointMediaQueries(theme, [
                css`font-size: 14px;`,
                css`font-size: 14px;`,
                css`font-size: 14px;`,
                null,
                null,
            ])}           
        }
    `,
    },
};

const styles = ADImyListStyles;

type Props = OwnProps & StateProps & ResolveThunks<typeof mapDispatchToProps>;

const RecentWishLists: React.FC<Props> = (props) => {
    const container = useRef<HTMLDivElement>(null);
    const {
        recentWishListsDataView: { value: recentWishLists },
        myListsPageNavLink,
        isAuthenticated,
        isMylistAccess,
        loadWishLists,
        setData,
        fields,
    } = props;

    const loyaltyState = useSelector((state: CustomApplicationState) => state.ADIGlobalData.loyaltyRewardStore.loyalty);
    const userRoles = useSelector((state: CustomApplicationState) => state.context.session.userRoles);
    const isProRewardsUser = userRoles && userRoles.includes("ADIProRewards");
    const isValidHmiUser = isProRewardsUser && checkVaildUserForHmiRewards(loyaltyState);
    const loadingSpinner = useSelector(
        (state: CustomApplicationState) => state?.ADIGlobalData?.loyaltyRewardStore.isLoading,
    );
    
    useEffect(() => {
        if (!isMylistAccess && isAuthenticated &&  (!loadingSpinner || !isProRewardsUser)) {
            const parentElement = container.current!.parentElement!.parentElement!.parentElement;
            if (parentElement) {
                parentElement.classList.add('isTwoColum');
            }
        }
    }, [recentWishLists, isMylistAccess]);

    useEffect(() => {
        recentWishListsParameter.pageSize = fields.numberOfRecords;
        recentWishListsParameter.filter = fields.pageType;
        if (isAuthenticated && isMylistAccess && !recentWishLists && !props.recentWishListsDataView.isLoading && !isValidHmiUser) {
            loadWishLists(recentWishListsParameter);
        }
    }, [fields.numberOfRecords, fields.pageType,isAuthenticated,isMylistAccess, recentWishLists]);

    const formattedDateInGrid = (dateString: any) => {
        if (!dateString) {
            return "";
        }
        const newDate = new Date(dateString);
        const splitedDate = newDate.toString().split(" ");
        let finalDate = "";
        if (splitedDate) {
            finalDate = `${splitedDate[1]}. ${splitedDate[2]}, ${splitedDate[3]}`;
        }
        return finalDate;
    };

    return (
        <>  <StyledWrapper ref={container}>
            {isMylistAccess && isAuthenticated && !isValidHmiUser &&
                <StyledWrapper {...styles.myListGridContainer}>
                    {
                        <GridContainer {...styles.myListGridCard}>
                        <GridItem width={[12]} {...styles.myListTitleContainer}>
                            <RecentWishListsIcon />
                            <Typography {...styles.myListTitle}>{translate("My Lists")}</Typography>
                        </GridItem>
                        <GridItem width={[12]} {...styles.myListCardContainer}>
                            <GridContainer {...styles.myListCardGridContainer}>
                                <GridItem {...styles.myListContentTittle}>
                                    <Typography {...styles.myListContentTitle}>{translate("Your favorite products")}</Typography>
                                    <Typography {...styles.myListContentDescription}>{translate("Easily keep track of products you’re interested in without the need to commit to a purchase immediately. Revisit your Lists when ready to make a decision.")}</Typography>
                                </GridItem>
                                
                                <>
                                    <GridItem>
                                        {recentWishLists && recentWishLists.map(wishList => (
                                            <StyledWrapper key={wishList.id} {...styles.myListContentGridItem}> {}
                                                <StyledWrapper>
                                                    <Typography {...styles.myListContentTittle}>{formattedDateInGrid(wishList.updatedOn)}</Typography>
                                                    <Typography {...styles.myListContentDivider}>|</Typography>
                                                    <Typography {...styles.addToListName}>{wishList.name}</Typography>
                                                </StyledWrapper>
                                                <Typography {...styles.myListContenDetails}>
                                                    <Link href={myListsPageNavLink?.url} onClick={() => setData(wishList)}>
                                                        {translate("Details")}<ChevronRight /> 
                                                    </Link>
                                                </Typography>
                                            </StyledWrapper>
                                        ))}
                                    </GridItem>
                                </>
                                {recentWishLists &&
                                <GridItem {...styles.myListBtnGriditem}>
                                    <Link {...styles.myListBtnStyle}
                                        href={myListsPageNavLink?.url}>
                                        {translate("See All Lists")}
                                    </Link>
                                </GridItem> 
                                }
                            </GridContainer>
                        </GridItem>
                    </GridContainer>
                    }
                </StyledWrapper >
                }
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentWishLists),
    definition: {
        group: "Common",
        displayName: "ADI MyList",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Lists Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 2,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.pageType,
                displayName: "Page Type",
                editorTemplate: "TextField",
                defaultValue: "HomePage",
                fieldType: "General",
                sortOrder: 2,
            },
        ],
    },
};

export default widgetModule;
